import { Store01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { startOfToday, subDays } from "date-fns";
import { lazy, Suspense, useCallback, useMemo } from "react";
import { PageContent } from "@/components/layout/page-content";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import {
	type ComboboxOption,
	MultiSelectCombobox,
} from "@/components/ui/multi-select-combobox";
import {
	type DateRange,
	DateRangePicker,
} from "@/components/ui/range-date-picker";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMe } from "@/hooks/use-me";

const CannabisTab = lazy(async () => {
	const m = await import("@/features/analytics/_components/cannabis-tab");
	return { default: m.CannabisTab };
});
const CustomersTab = lazy(async () => {
	const m = await import("@/features/analytics/_components/customers-tab");
	return { default: m.CustomersTab };
});
const GeneralTab = lazy(async () => {
	const m = await import("@/features/analytics/_components/general-tab");
	return { default: m.GeneralTab };
});
const InventoryTab = lazy(async () => {
	const m = await import("@/features/analytics/_components/inventory-tab");
	return { default: m.InventoryTab };
});
const LeaderboardTab = lazy(async () => {
	const m = await import("@/features/analytics/_components/leaderboard-tab");
	return { default: m.LeaderboardTab };
});

const tabFallback = (
	<div className="flex items-center justify-center py-16">
		<Spinner className="size-5" />
	</div>
);

import type { AnalyticsTab } from "@/lib/types/analytics";

interface AnalyticsSearch {
	tab?: AnalyticsTab;
	branches?: string;
	startDate?: string;
	endDate?: string;
	[key: string]: unknown;
}

export const Route = createFileRoute("/(dashboard)/analytics")({
	component: AnalyticsPage,
	validateSearch: (search: Record<string, unknown>): AnalyticsSearch => ({
		tab: (search.tab as AnalyticsTab) || "general",
		branches: (search.branches as string) || "",
		startDate: search.startDate as string | undefined,
		endDate: search.endDate as string | undefined,
	}),
});

function formatLocalDate(date: Date) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

function AnalyticsPage() {
	const navigate = useNavigate();
	const {
		tab,
		branches: branchesParam,
		startDate,
		endDate,
	} = useSearch({
		from: "/(dashboard)/analytics",
	});

	// Parse branch IDs from URL
	const selectedBranchIds = useMemo(() => {
		if (!branchesParam) return [];
		return branchesParam.split(",").filter(Boolean);
	}, [branchesParam]);

	// Parse date range from URL
	const dateRange = useMemo<DateRange | undefined>(() => {
		if (startDate && endDate) {
			return {
				from: new Date(startDate),
				to: new Date(endDate),
			};
		}
		// Default to last 30 days
		return {
			from: subDays(startOfToday(), 29),
			to: startOfToday(),
		};
	}, [startDate, endDate]);

	// Get current user and their active organization
	const me = useMe();
	const orgId = me?.activeOrganizationId;

	// Query branches for the organization
	const branchesData = useQuery(
		api.features.branches.web.list,
		orgId ? { orgId } : "skip",
	);
	const branches = branchesData ?? [];

	// Branch options for multi-select
	const branchOptions: ComboboxOption[] = useMemo(
		() => branches.map((b) => ({ value: b._id, label: b.name })),
		[branches],
	);

	// Get all branch IDs for "all branches" selection
	const allBranchIds = useMemo(
		() => branches.map((b) => b._id as Id<"branches">),
		[branches],
	);

	// Build query arguments for date-based analytics
	const queryArgs = useMemo(() => {
		if (!orgId || allBranchIds.length === 0) return null;
		return {
			orgId,
			branchIds:
				selectedBranchIds.length > 0
					? (selectedBranchIds as Id<"branches">[])
					: allBranchIds,
			startDate: dateRange?.from ? formatLocalDate(dateRange.from) : "",
			endDate: dateRange?.to ? formatLocalDate(dateRange.to) : "",
		};
	}, [orgId, selectedBranchIds, allBranchIds, dateRange]);

	// Query analytics data
	const generalData = useQuery(
		api.features.analytics.web.getGeneralAnalytics,
		queryArgs ?? "skip",
	);

	const customerData = useQuery(
		api.features.analytics.web.getCustomerAnalytics,
		queryArgs ?? "skip",
	);

	const cannabisData = useQuery(
		api.features.analytics.web.getCannabisAnalytics,
		queryArgs ?? "skip",
	);

	const leaderboardData = useQuery(
		api.features.analytics.web.getLeaderboardAnalytics,
		queryArgs ?? "skip",
	);

	// Inventory doesn't need date range
	const inventoryQueryArgs = useMemo(() => {
		if (!orgId || allBranchIds.length === 0) return null;
		return {
			orgId,
			branchIds:
				selectedBranchIds.length > 0
					? (selectedBranchIds as Id<"branches">[])
					: allBranchIds,
		};
	}, [orgId, selectedBranchIds, allBranchIds]);

	const inventoryData = useQuery(
		api.features.analytics.web.getInventoryAnalytics,
		inventoryQueryArgs ?? "skip",
	);

	const isSyncing = !queryArgs;
	const isLoadingGeneral = isSyncing || generalData === undefined;
	const isLoadingCustomers = isSyncing || customerData === undefined;
	const isLoadingCannabis = isSyncing || cannabisData === undefined;
	const isLoadingLeaderboard = isSyncing || leaderboardData === undefined;
	const isLoadingInventory = !inventoryQueryArgs || inventoryData === undefined;

	// Show cannabis tab only if it's not explicitly disabled (null)
	const showCannabisTab = cannabisData !== null;

	// Handle tab change
	const handleTabChange = useCallback(
		(newTab: string) => {
			if (newTab) {
				navigate({
					to: ".",
					search: (prev) => {
						return {
							...prev,
							tab: newTab as AnalyticsTab,
						};
					},
				});
			}
		},
		[navigate],
	);

	// Handle branch selection change
	const handleBranchesChange = useCallback(
		(selected: string[]) => {
			navigate({
				to: ".",
				search: (prev) => {
					return {
						...prev,
						branches: selected.join(","),
					};
				},
			});
		},
		[navigate],
	);

	// Handle date range change
	const handleDateRangeChange = useCallback(
		(range: DateRange | undefined) => {
			navigate({
				to: ".",
				search: (prev) => {
					return {
						...prev,
						startDate: range?.from ? formatLocalDate(range.from) : undefined,
						endDate: range?.to ? formatLocalDate(range.to) : undefined,
					};
				},
			});
		},
		[navigate],
	);

	if (branchesData !== undefined && branches.length === 0) {
		return (
			<PageContent title="Analytics">
				<EmptyStateWithSkeleton
					title="No branches yet"
					description="Create a branch to start seeing analytics for your organization."
					actionLabel="Create branch"
					onAction={() => navigate({ to: "/settings/branches" })}
					icon={
						<HugeiconsIcon
							icon={Store01Icon}
							className="h-5 w-5 text-neutral-700"
							strokeWidth={2.2}
						/>
					}
				/>
			</PageContent>
		);
	}

	return (
		<PageContent title="Analytics">
			<div className="flex flex-wrap items-center justify-start gap-3 mb-6">
				<MultiSelectCombobox
					options={branchOptions}
					selected={selectedBranchIds}
					onChange={handleBranchesChange}
					placeholder="All branches"
					searchPlaceholder="Search branches..."
					emptyMessage="No branches found"
					className="w-48"
					shortcut="B"
					createNewPrefix="branch"
				/>
				<DateRangePicker value={dateRange} onChange={handleDateRangeChange} />
			</div>

			<ErrorBoundary
				fallback={
					<p className="text-sm text-muted-foreground py-8 text-center">
						Failed to load analytics. Please refresh the page.
					</p>
				}
			>
				<Tabs value={tab} onValueChange={handleTabChange}>
					<TabsList variant="line" className="gap-8 text-neutral-500">
						<TabsTrigger value="general" className="px-0 pb-2 font-medium">
							General
						</TabsTrigger>
						<TabsTrigger value="customers" className="px-0 pb-2 font-medium">
							Customers
						</TabsTrigger>
						{showCannabisTab && (
							<TabsTrigger value="cannabis" className="px-0 pb-2 font-medium">
								Cannabis
							</TabsTrigger>
						)}
						<TabsTrigger value="leaderboard" className="px-0 pb-2 font-medium">
							Leaderboard
						</TabsTrigger>
						<TabsTrigger value="inventory" className="px-0 pb-2 font-medium">
							Inventory
						</TabsTrigger>
					</TabsList>

					<TabsContent value="general" className="mt-4">
						<Suspense fallback={tabFallback}>
							<GeneralTab
								data={generalData}
								loading={isLoadingGeneral}
								branches={branches
									.filter(
										(b) =>
											selectedBranchIds.length === 0 ||
											selectedBranchIds.includes(b._id),
									)
									.map((b) => ({ id: b._id, name: b.name }))}
							/>
						</Suspense>
					</TabsContent>

					<TabsContent value="customers" className="mt-4">
						<Suspense fallback={tabFallback}>
							<CustomersTab data={customerData} loading={isLoadingCustomers} />
						</Suspense>
					</TabsContent>

					{showCannabisTab && (
						<TabsContent value="cannabis" className="mt-4">
							<Suspense fallback={tabFallback}>
								<CannabisTab
									data={cannabisData ?? undefined}
									loading={isLoadingCannabis}
								/>
							</Suspense>
						</TabsContent>
					)}

					<TabsContent value="leaderboard" className="mt-4">
						<Suspense fallback={tabFallback}>
							<LeaderboardTab
								data={leaderboardData}
								loading={isLoadingLeaderboard}
							/>
						</Suspense>
					</TabsContent>

					<TabsContent value="inventory" className="mt-4">
						<Suspense fallback={tabFallback}>
							<InventoryTab data={inventoryData} loading={isLoadingInventory} />
						</Suspense>
					</TabsContent>
				</Tabs>
			</ErrorBoundary>
		</PageContent>
	);
}
